import Phaser from 'phaser';
import * as ABCya from '@abcya/abcya_phaser';
import CubeButton, { CubeButtonEvents } from '../CubeButton';
import UIAssetKeys from '../UIAssetKeys';
import GameData, { getAreasInRegion, getFoundAreasInRegion, getFoundShapesInRegion, getTotalFoundShapes, getTotalRequiredShapes, REGIONS, saveGameData } from '../gameData';
import FontKeys from '../FontKeys';
import { GameEvents, GameStates } from '../scenes/Game';
import SoundAssetKeys from '../SoundAssetKeys';

/**
 * The menu popup is an overlay over the entire game and can be brought up at any time and allows the player to go to specific region states.
 */
export default class MenuPopup extends Phaser.GameObjects.Container
{
    /**
     * Create the Menu popup overlay
     * @param {Phaser.Scene} scene the owner scene
     */
    constructor(scene)
    {
        super(scene);

        this.scene.add.existing(this);

        this.add(this.overlay = this.scene.add.rectangle(0, 0, this.scene.scale.width, this.scene.scale.height, 0x306379, 0.5).setOrigin(0, 0).setInteractive());
        this.background = ABCya.Images.addImage((this.scene.scale.width / 2), (this.scene.scale.height / 2), UIAssetKeys._06_Menu_Pop_up_MnuPopUpBG, this);

        this._createHeaderButtons();

        this._createRegionInformation(REGIONS.BEACH, 232, 291, UIAssetKeys._06_Menu_Pop_up_Beach_Badge, UIAssetKeys._06_Menu_Pop_up_Beach_Cntr, 0x5f5fd6);
        this._createRegionInformation(REGIONS.DOWNTOWN, 421, 291, UIAssetKeys._06_Menu_Pop_up_City_Badge, UIAssetKeys._06_Menu_Pop_up_City_Cntr, 0x2b4270);
        this._createRegionInformation(REGIONS.CAMP, 610, 291, UIAssetKeys._06_Menu_Pop_up_Camp_Badge, UIAssetKeys._06_Menu_Pop_up_Camp_Cntr, 0x204243);
        this._createRegionInformation(REGIONS.SPACE, 802, 291, UIAssetKeys._06_Menu_Pop_up_Space_Badge, UIAssetKeys._06_Menu_Pop_up_Space_Cntr, 0x662e63);

        this.setRegionState(REGIONS.BEACH, false);
        this.setRegionState(REGIONS.DOWNTOWN, true);
        this.setRegionState(REGIONS.CAMP, true);
        this.setRegionState(REGIONS.SPACE, true);

        //this._addReference("MenuRef1", "assets/reference_images/5 Get In Shape_MenuPopup_1-01.jpg");
        //this._addReference("MenuRef1", "assets/reference_images/5 Get In Shape_MenuPopup_2-01.jpg");

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        this.setAlpha(0);
        this.toggleButtons(false);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        this.setAlpha(0.000001);
        this.toggleButtons(false);
        this.refreshRegionInfo();
    }

    /**
     * Show the menu.
     */
    enter()
    {
        ABCya.Tweens.addTween(this, 500, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out, () => 
        {
            this.toggleButtons(true);
            this.emit(MenuPopupEvents.ENTER_TWEEN_COMPLETE);

            let tweens = [
                { targets: this.letterP, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterP.y - 10 }, offset: 0 },
                { targets: this.letterR1, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterR1.y - 10 }, offset: 100 },
                { targets: this.letterO, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterO.y - 10 }, offset: 200 },
                { targets: this.letterG, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterG.y - 10 }, offset: 300 },
                { targets: this.letterR2, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterR2.y - 10 }, offset: 400 },
                { targets: this.letterE, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterE.y - 10 }, offset: 500 },
                { targets: this.letterS1, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterS1.y - 10 }, offset: 600 },
                { targets: this.letterS2, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { y: this.letterS2.y - 10 }, offset: 700 },

            ];

            this.progressAnimation = this.scene.tweens.timeline({
                tweens: tweens,
                yoyo: true,
                loop: -1,
                loopDelay: 5000
            });
        });
    }

    /**
     * Exit the menu.
     */
    leave()
    {
        this.toggleButtons(false);

        for (var i = 0; i < this.progressAnimation.totalData; i++)
        {
            var tween = this.progressAnimation.data[i];

            if (tween.hasStarted)
            {
                tween.data.forEach((data) =>
                {
                    data.target[data.key] = data.start;
                });
            }
        }
        this.progressAnimation.stop();

        ABCya.Tweens.addTween(this, 500, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In, () => 
        {
            this.emit(MenuPopupEvents.LEAVE_TWEEN_COMPLETE);
        });
    }

    /**
     * Sets the buttons on the menu to be enabled or disabled.
     * @param {Boolean} enable true if the buttons should be enabled, false otherwise
     */
    toggleButtons(enable)
    {
        if (enable)
        {
            this.homeButton.setInteractive();
            this.shapeReviewButton.setInteractive();
            this.soundButton.setInteractive();
            this.backButton.setInteractive();

            if (!this[REGIONS.BEACH].isLocked) this[REGIONS.BEACH].icon.setInteractive();
            if (!this[REGIONS.DOWNTOWN].isLocked) this[REGIONS.DOWNTOWN].icon.setInteractive();
            if (!this[REGIONS.CAMP].isLocked) this[REGIONS.CAMP].icon.setInteractive();
            if (!this[REGIONS.SPACE].isLocked) this[REGIONS.SPACE].icon.setInteractive();
        }
        else
        {
            this.homeButton.disableInteractive();
            this.shapeReviewButton.disableInteractive();
            this.soundButton.disableInteractive();
            this.backButton.disableInteractive();

            this[REGIONS.BEACH].icon.disableInteractive();
            this[REGIONS.DOWNTOWN].icon.disableInteractive();
            this[REGIONS.CAMP].icon.disableInteractive();
            this[REGIONS.SPACE].icon.disableInteractive();
        }
    }

    /**
     * Sets the locked status of a region. A locked region has some of its data hidden.
     * @param {string} region the REGIONS enum value
     * @param {Boolean} isLocked true if the region is locked, false otherwise
     */
    setRegionState(region, isLocked)
    {
        let regionInfo = this[region];
        regionInfo.isLocked = isLocked;
        regionInfo.icon.setFrame(isLocked ? UIAssetKeys._06_Menu_Pop_up_Lock_Badge : regionInfo.iconKey);
        let shapeRequirementText = GameData.region_unlock_requirements[region] - getTotalFoundShapes();
        regionInfo.shapeRequirementText.text = isLocked ? shapeRequirementText : "";
        regionInfo.areasHeader.setColor(isLocked ? "#444444" : regionInfo.fontColor);
        regionInfo.shapesHeader.setColor(isLocked ? "#444444" : regionInfo.fontColor);
        regionInfo.areasBg.setFrame(isLocked ? UIAssetKeys._06_Menu_Pop_up_Lock_QMark : regionInfo.circleBgKey);
        regionInfo.shapesBg.setFrame(isLocked ? UIAssetKeys._06_Menu_Pop_up_Lock_QMark : regionInfo.circleBgKey);
        regionInfo.shapesText.text = isLocked ? "" : getFoundShapesInRegion(region) + "/" + getTotalRequiredShapes(region);
        regionInfo.areasText.text = isLocked ? "" : getFoundAreasInRegion(region) + "/" + getAreasInRegion(region);
    }

    /**
     * Refreshes the region information and locked status of all regions.
     */
    refreshRegionInfo()
    {
        this.setRegionState(REGIONS.BEACH, false);
        this.setRegionState(REGIONS.DOWNTOWN, GameData.region_unlock_requirements[REGIONS.DOWNTOWN] > getTotalFoundShapes());
        this.setRegionState(REGIONS.CAMP, GameData.region_unlock_requirements[REGIONS.CAMP] > getTotalFoundShapes());
        this.setRegionState(REGIONS.SPACE, GameData.region_unlock_requirements[REGIONS.SPACE] > getTotalFoundShapes());
    }

    /**
     * Creates the header buttons (home/back/mute/etc) and needed listeners.
     */
    _createHeaderButtons()
    {
        this.homeButton = new CubeButton(this.scene, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home3, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home2, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home1);
        this.homeButton.setPosition(180, 152).setScale(0.7397);
        this.homeButton.on(CubeButtonEvents.CLICKED, () =>
        {
            this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.TITLE);
            this.leave();
        });

        this.shapeReviewButton = new CubeButton(this.scene, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Home3, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_ShpRvw2, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_ShpRvw1);
        this.shapeReviewButton.setPosition(270, 152).setScale(0.7397);
        this.shapeReviewButton.on(CubeButtonEvents.CLICKED, () =>
        {
            this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.SHAPE_REVIEW);
            this.leave();
        });

        this.soundButton = new CubeButton(this.scene, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOn3, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOn2, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOn1);
        this.soundButton.on(CubeButtonEvents.CLICKED, () =>
        {
            this.scene.events.emit(GameEvents.TOGGLE_SOUND, !GameData.enableSound);
        });
        this.soundButton.setPosition(760, 152);
        this.scene.events.on(GameEvents.TOGGLE_SOUND, (isSoundEnabled) =>
        {
            GameData.enableSound = isSoundEnabled;
            this.soundButton.rightSide.setFrame(GameData.enableSound ? UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOn1 : UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOff1);
            this.soundButton.leftSide.setFrame(GameData.enableSound ? UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOn2 : UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOff2);
            this.scene.sound.mute = !GameData.enableSound;
        });

        this.backButton = new CubeButton(this.scene, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_SndOn3, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Back2_1, UIAssetKeys._Buttons_Menu_Pop_Up_Btn_Back1);
        this.backButton.setPosition(844, 152);
        this.backButton.on(CubeButtonEvents.CLICKED, () =>
        {
            this.leave();
        });

        this.add([this.homeButton, this.shapeReviewButton, this.soundButton, this.backButton]);

        this.letterP = ABCya.Images.addImage(362, 148, UIAssetKeys._06_Menu_Pop_up_Progress_P_1, this).setScale(0.78);
        this.letterR1 = ABCya.Images.addImage(407, 148, UIAssetKeys._06_Menu_Pop_up_Progress_R, this).setScale(0.78);
        this.letterO = ABCya.Images.addImage(451, 148, UIAssetKeys._06_Menu_Pop_up_Progress_O_2, this).setScale(0.78);
        this.letterG = ABCya.Images.addImage(495, 148, UIAssetKeys._06_Menu_Pop_up_Progress_G, this).setScale(0.78);
        this.letterR2 = ABCya.Images.addImage(540, 148, UIAssetKeys._06_Menu_Pop_up_Progress_R, this).setScale(0.78);
        this.letterE = ABCya.Images.addImage(584, 148, UIAssetKeys._06_Menu_Pop_up_Progress_E_1, this).setScale(0.78);
        this.letterS1 = ABCya.Images.addImage(628, 148, UIAssetKeys._06_Menu_Pop_up_Progress_S_1, this).setScale(0.78);
        this.letterS2 = ABCya.Images.addImage(673, 148, UIAssetKeys._06_Menu_Pop_up_Progress_S_1, this).setScale(0.78);
    }

    /**
     * 
     * @param {string} region the REGIONS enum value
     * @param {number} x the x position of the region information
     * @param {number} y the y position of the region information
     * @param {string} iconKey the region icon key (found in the UIAssetKeys)
     * @param {string} circleBgKey the region area/shape text circle background key (found in the UIAssetKeys)
     * @param {number} fontColor the text font color
     */
    _createRegionInformation(region, x, y, iconKey, circleBgKey, fontColor)
    {
        let regionInfo =
        {
            iconKey: iconKey,
            circleBgKey: circleBgKey,
            fontColor: Phaser.Display.Color.IntegerToColor(fontColor).rgba,
            /**@type {Phaser.GameObjects.Image} */
            icon: null,
            /**@type {Phaser.GameObjects.Text} */
            shapeRequirementText: null,
            /**@type {Phaser.GameObjects.Text} */
            areasHeader: null,
            /**@type {Phaser.GameObjects.Image} */
            areasBg: null,
            /**@type {Phaser.GameObjects.Text} */
            areasText: null,
            /**@type {Phaser.GameObjects.Text} */
            shapesHeader: null,
            /**@type {Phaser.GameObjects.Image} */
            shapesBg: null,
            /**@type {Phaser.GameObjects.Text} */
            shapesText: null,
            /**@type {Boolean} */
            isLocked: true,
        };

        regionInfo.icon = ABCya.Images.addImage(x, y, iconKey, this).setInteractive({ useHandCursor: true });
        regionInfo.icon.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
        {
            this.leave();
            this.scene.events.emit(GameEvents.GO_TO_STATE, region);
            ABCya.Audio.playOneShot(SoundAssetKeys._click, this.scene);
        });

        regionInfo.shapeRequirementText = ABCya.Text.addText(x, y - 22, 21, 0x58595b, FontKeys._graystrokeregular, "", this);

        regionInfo.areasHeader = ABCya.Text.addText(x, y + 110, 32, fontColor, FontKeys._retrigaregular, GameData.text.areasExplored, this);
        regionInfo.areasBg = ABCya.Images.addImage(x, y + 180, circleBgKey, this);
        let areaText = getFoundAreasInRegion(region) + "/" + getAreasInRegion(region);
        regionInfo.areasText = ABCya.Text.addText(x, y + 180, 20, 0xffffff, FontKeys._graystrokeregular, areaText, this);

        regionInfo.shapesHeader = ABCya.Text.addText(x, y + 263, 32, fontColor, FontKeys._retrigaregular, GameData.text.shapesFound, this);
        regionInfo.shapesBg = ABCya.Images.addImage(x, y + 332, circleBgKey, this);
        let shapesText = getFoundShapesInRegion(region) + "/" + getTotalRequiredShapes(region);
        regionInfo.shapesText = ABCya.Text.addText(x, y + 332, 20, 0xffffff, FontKeys._graystrokeregular, shapesText, this);

        this[region] = regionInfo;
    }

    /**
     * Add a reference image.
     * @param {string} key the unique key to use for this reference (in case you want to access it elsewhere)
     * @param {string} path the relative path to the reference image
     * @param {Boolean} enablePlacer whether to enable the image placer debug tool after adding the reference image
     */
    _addReference(key, path, enablePlacer = true)
    {
        ABCya.Debug.addScreenReference(this.scene, key, path, (image) =>
        {
            if (enablePlacer) ABCya.Debug.enableImagePlacerDebug(this.scene, this.scene.background, image);
        });
    }
}

/**
 * Menu popup events.
 */
export const MenuPopupEvents = {
    ENTER_TWEEN_COMPLETE: "entertweencomplete",
    LEAVE_TWEEN_COMPLETE: "leavetweencomplete",
}